import { Fragment, useState, useEffect } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { songsApi } from '../../api/songs';
import { useAudioStore } from '../../stores/audioStore';
import type { SongSummary } from '../../types';

interface WelcomeMarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Extended markdown renderer for welcome page
 * Supports all basic markdown PLUS:
 * - [audio:123] - Inline audio player with album art
 * - [image:123] - Album artwork embed
 */
export function WelcomeMarkdownRenderer({ content, className = '' }: WelcomeMarkdownRendererProps) {
  const [songCache, setSongCache] = useState<Map<number, SongSummary>>(new Map());
  const [loadingSongs, setLoadingSongs] = useState<Set<number>>(new Set());

  // Extract all song IDs from content
  const extractSongIds = (text: string): number[] => {
    const ids: number[] = [];
    const audioMatches = text.matchAll(/\[audio:(\d+)\]/g);
    const imageMatches = text.matchAll(/\[image:(\d+)\]/g);

    for (const match of audioMatches) {
      ids.push(parseInt(match[1]));
    }
    for (const match of imageMatches) {
      ids.push(parseInt(match[1]));
    }

    return [...new Set(ids)]; // Unique IDs
  };

  // Pre-load all song data
  useEffect(() => {
    const songIds = extractSongIds(content);

    for (const id of songIds) {
      if (!songCache.has(id) && !loadingSongs.has(id)) {
        setLoadingSongs(prev => new Set(prev).add(id));

        songsApi.getSongById(id)
          .then(song => {
            setSongCache(prev => new Map(prev).set(id, song));
            setLoadingSongs(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          })
          .catch(err => {
            console.error(`Failed to load song ${id}:`, err);
            setLoadingSongs(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          });
      }
    }
  }, [content]);

  const renderMarkdown = (text: string): JSX.Element[] => {
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let listItems: string[] = [];
    let lineIndex = 0;

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} className="list-disc list-inside space-y-1 my-3 text-gray-300">
            {listItems.map((item, idx) => (
              <li key={idx}>{processInlineMarkdown(item)}</li>
            ))}
          </ul>
        );
        listItems = [];
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      lineIndex = i;

      // Check for embeds first (must be on their own line)
      const audioMatch = line.match(/^\[audio:(\d+)\]$/);
      if (audioMatch) {
        flushList();
        const songId = parseInt(audioMatch[1]);
        elements.push(<AudioEmbed key={`audio-${i}`} songId={songId} song={songCache.get(songId)} />);
        continue;
      }

      const imageMatch = line.match(/^\[image:(\d+)\]$/);
      if (imageMatch) {
        flushList();
        const songId = parseInt(imageMatch[1]);
        elements.push(<ImageEmbed key={`image-${i}`} songId={songId} song={songCache.get(songId)} />);
        continue;
      }

      // Heading level 1 (#)
      if (line.startsWith('# ')) {
        flushList();
        const headingText = line.substring(2);
        elements.push(
          <h1 key={`h1-${i}`} className="text-3xl font-bold text-white mt-8 mb-4 first:mt-0">
            {processInlineMarkdown(headingText)}
          </h1>
        );
        continue;
      }

      // Heading level 2 (##)
      if (line.startsWith('## ')) {
        flushList();
        const headingText = line.substring(3);
        elements.push(
          <h2 key={`h2-${i}`} className="text-2xl font-semibold text-gray-100 mt-6 mb-3">
            {processInlineMarkdown(headingText)}
          </h2>
        );
        continue;
      }

      // Heading level 3 (###)
      if (line.startsWith('### ')) {
        flushList();
        const headingText = line.substring(4);
        elements.push(
          <h3 key={`h3-${i}`} className="text-xl font-semibold text-gray-200 mt-5 mb-2">
            {processInlineMarkdown(headingText)}
          </h3>
        );
        continue;
      }

      // Bullet list items
      if (line.match(/^[\s]*[-*]\s+/)) {
        const itemText = line.replace(/^[\s]*[-*]\s+/, '');
        listItems.push(itemText);
        continue;
      }

      // Numbered list items
      if (line.match(/^[\s]*\d+\.\s+/)) {
        flushList();
        // Simple numbered list - could be enhanced later
        elements.push(
          <p key={`num-${i}`} className="text-gray-300 leading-relaxed">
            {processInlineMarkdown(line)}
          </p>
        );
        continue;
      }

      // Flush any pending list before non-list content
      flushList();

      // Empty line
      if (line === '') {
        elements.push(<br key={`br-${i}`} />);
        continue;
      }

      // Regular paragraph
      elements.push(
        <p key={`p-${i}`} className="text-gray-300 leading-relaxed mb-3">
          {processInlineMarkdown(line)}
        </p>
      );
    }

    flushList();
    return elements;
  };

  const processInlineMarkdown = (text: string): (string | JSX.Element)[] => {
    const parts: (string | JSX.Element)[] = [];
    let remaining = text;
    let keyIndex = 0;

    while (remaining.length > 0) {
      // Bold (**text**)
      const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
      if (boldMatch) {
        parts.push(
          <strong key={`bold-${keyIndex++}`} className="font-bold text-white">
            {boldMatch[1]}
          </strong>
        );
        remaining = remaining.substring(boldMatch[0].length);
        continue;
      }

      // Italic (*text* or _text_)
      const italicMatch = remaining.match(/^[*_](.+?)[*_]/);
      if (italicMatch) {
        parts.push(
          <em key={`italic-${keyIndex++}`} className="italic">
            {italicMatch[1]}
          </em>
        );
        remaining = remaining.substring(italicMatch[0].length);
        continue;
      }

      // Regular text until next markdown symbol
      const nextSpecial = remaining.search(/[*_]/);
      if (nextSpecial === -1) {
        parts.push(remaining);
        break;
      } else if (nextSpecial === 0) {
        parts.push(remaining[0]);
        remaining = remaining.substring(1);
      } else {
        parts.push(remaining.substring(0, nextSpecial));
        remaining = remaining.substring(nextSpecial);
      }
    }

    return parts;
  };

  return (
    <div className={className}>
      {renderMarkdown(content).map((element, idx) => (
        <Fragment key={idx}>{element}</Fragment>
      ))}
    </div>
  );
}

// Audio player embed component
function AudioEmbed({ songId, song }: { songId: number; song?: SongSummary }) {
  const { currentSong, isPlaying, playSong, pause, resume } = useAudioStore();
  const isCurrentSong = currentSong?.id === songId;

  if (!song) {
    return (
      <div className="my-4 p-4 bg-monster-card border border-monster-border rounded-lg flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-monster-blue mr-2" />
        <span className="text-gray-400">Loading audio...</span>
      </div>
    );
  }

  const handlePlayPause = () => {
    if (isCurrentSong) {
      if (isPlaying) {
        pause();
      } else {
        resume();
      }
    } else {
      playSong(song, true);
    }
  };

  return (
    <div className="my-4 p-4 bg-monster-card border border-monster-border rounded-lg hover:border-monster-blue/50 transition-colors">
      <div className="flex items-center gap-4">
        {/* Album art thumbnail */}
        <img
          src={song.imagePath}
          alt={song.title}
          className="w-16 h-16 rounded object-cover flex-shrink-0"
        />

        {/* Song info */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white truncate">{song.title}</p>
          <p className="text-sm text-gray-400 truncate">{song.artist}</p>
        </div>

        {/* Play button */}
        <button
          onClick={handlePlayPause}
          className="w-12 h-12 flex items-center justify-center rounded-full bg-monster-blue hover:bg-blue-600 transition-colors flex-shrink-0"
        >
          {isCurrentSong && isPlaying ? (
            <Pause size={20} className="text-white" />
          ) : (
            <Play size={20} className="text-white ml-0.5" />
          )}
        </button>
      </div>
    </div>
  );
}

// Image embed component
function ImageEmbed({ song }: { songId: number; song?: SongSummary }) {
  if (!song) {
    return (
      <div className="my-4 p-8 bg-monster-card border border-monster-border rounded-lg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-monster-blue mr-2" />
        <span className="text-gray-400">Loading image...</span>
      </div>
    );
  }

  return (
    <div className="my-4">
      <img
        src={song.imagePath}
        alt={song.title}
        className="w-full max-w-md mx-auto rounded-lg shadow-lg"
      />
      <p className="text-center text-sm text-gray-400 mt-2">
        {song.title} - {song.artist}
      </p>
    </div>
  );
}
